import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Dropdown,
  DropdownItem,
  Flex,
  InlineAlert,
  InputLabel,
  PortalContextProvider,
} from '@amzn/storm-ui';
import didElementLoad from 'src/commons/utils/didElementLoad';
import { LanguageInfo, selectSignOutInfo } from '../../store/slice';
import handleLanguageSwitch from './handle-language-switch';
import { useHeaderBundle } from 'src/providers/PantherLocalizationProvider';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import {
  AlertContainer,
  DropdownContainer,
} from 'src/features/header/components/styles';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

const EngSupportOnlyMessage =
  "The tool you're using is only available in English";

const LanguageAlert: React.VFC<{ message: string }> = ({
  message,
}): React.JSX.Element => (
  <AlertContainer>
    <InlineAlert variant="info" message={message} />
  </AlertContainer>
);

const LanguageSwitcher: React.VFC<LanguageInfo> = ({
  label,
  languageOptions,
  currentLocale,
  changeLanguageText,
}) => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const signOutInfo = useSelector(selectSignOutInfo);
  const [currentSelectedLocale, setCurrentSelectedLocale] =
    useState(currentLocale);

  const engSupportOnly =
    languageOptions.length == 1 && languageOptions[0].locale == 'en_US';

  useEffect(() => {
    didElementLoad('LanguageSwitcher');
  }, []);

  const handleLangSelection = (lang) => setCurrentSelectedLocale(lang);
  const handleLangChange = async () => {
    await handleLanguageSwitch(
      currentLocale,
      currentSelectedLocale,
      signOutInfo.url,
    );
  };

  const portalRef = React.useRef<HTMLDivElement>(null);
  const LanguageSwitcherOptions = languageOptions.map((language) => (
    <DropdownItem value={language.locale} key={language.locale}>
      {language.label}
    </DropdownItem>
  ));

  return (
    <Flex id="LanguageSwitcher" direction="column" rowGap="xxsmall">
      <InputLabel>
        {label.charAt(0) + label.substring(1).toLowerCase()}
      </InputLabel>
      {engSupportOnly ? (
        <LanguageAlert message={EngSupportOnlyMessage} />
      ) : (
        <>
          <Flex columnGap="xsmall">
            <DropdownContainer ref={portalRef}></DropdownContainer>
            <PortalContextProvider portalElementRef={portalRef}>
              <Dropdown
                noWrapDropdownItems
                name="aac-select-language"
                selectedValue={currentSelectedLocale}
                onChange={handleLangSelection}
                small={true}
                {...getTaktAttributes(
                  'aac-select-language',
                  chromeComponents.HEADER,
                  { locale: currentSelectedLocale },
                )}
              >
                {LanguageSwitcherOptions}
              </Dropdown>
            </PortalContextProvider>
            <Button
              disabled={currentSelectedLocale === currentLocale}
              onClick={handleLangChange}
              small={true}
              {...getTaktAttributes(
                'aac-language-switch-btn',
                chromeComponents.HEADER,
              )}
            >
              {changeLanguageText}
            </Button>
          </Flex>
          {
            // only render rodeo launch alert if the request is from rodeo
            internalChromeConfig?.isFromRodeo && (
              <LanguageAlert
                message={useHeaderBundle().getMessage(
                  'language-of-preference-alert-label',
                )}
              />
            )
          }
        </>
      )}
    </Flex>
  );
};

export default LanguageSwitcher;
